import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Clock } from 'lucide-react'
import type { PageProps, StaffMember, AvailabilityRule } from '@/types'
import { DAYS, HoursDayList } from '@/components/HoursDayList'

interface TeamAvailabilityProps extends PageProps {
  staff_member: StaffMember
  availability: AvailabilityRule[]
  business_availability: AvailabilityRule[]
  can_edit?: boolean
}

export default function TeamAvailability({
  staff_member,
  availability: initialAvailability,
  business_availability,
  can_edit = true,
  current_user,
  current_organization,
  current_business,
}: TeamAvailabilityProps) {
  const orgSlug = current_organization?.slug || ''
  const hasCustomHours = initialAvailability.some((r) => r.enabled)
  
  const [useCustom, setUseCustom] = useState(hasCustomHours)
  const [availability, setAvailability] = useState(
    hasCustomHours ? initialAvailability : business_availability
  )
  const [saving, setSaving] = useState(false)

  const handleUseCustomToggle = (checked: boolean) => {
    setUseCustom(checked)
    if (!checked) {
      setAvailability(business_availability)
    }
  }

  const handleSave = () => {
    setSaving(true)
    router.put(
      `/${orgSlug}/team/${staff_member.id}/update_availability`,
      {
        use_custom_hours: useCustom,
        availability: useCustom ? availability : [],
      },
      { onFinish: () => setSaving(false) }
    )
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/team`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al equipo
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            Horarios de {staff_member.name}
          </h1>
          <p className="text-muted-foreground">
            {can_edit
              ? 'Configura los horarios de disponibilidad de este miembro'
              : 'Horarios de disponibilidad de este miembro'}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card">
          {can_edit && (
          <div className="border-b border-border p-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={useCustom}
                onChange={(e) => handleUseCustomToggle(e.target.checked)}
              />
              <div>
                <p className="font-medium">Usar horarios personalizados</p>
                <p className="text-sm text-muted-foreground">
                  {useCustom
                    ? 'Este miembro tiene horarios diferentes al negocio'
                    : 'Usar los horarios predeterminados del negocio'}
                </p>
              </div>
            </label>
          </div>
          )}

          {useCustom && (
            <HoursDayList
              availability={availability}
              onChange={setAvailability}
              disabled={!can_edit}
            />
          )}

          {!useCustom && (
            <div className="p-4">
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4" />
                  Horarios del negocio
                </div>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {DAYS.map((day) => {
                    const rule = business_availability.find((r) => r.day_of_week === day.value)
                    return (
                      <div key={day.value} className="flex justify-between">
                        <span>{day.label}</span>
                        <span>
                          {rule?.enabled
                            ? `${rule.start_time} - ${rule.end_time}${
                                rule.pausa_enabled
                                  ? ` · Pausa ${rule.pausa_start_time}–${rule.pausa_end_time}`
                                  : ''
                              }`
                            : 'Cerrado'}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {can_edit && (
          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar horarios'}
            </Button>
            <Link href={`/${orgSlug}/team`}>
              <Button variant="outline">Cancelar</Button>
            </Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
